import { useCallback, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGamepadAlt as faGamepadAltSolid, faLock } from '@fortawesome/pro-solid-svg-icons';
import { faGamepadAlt as faGamepadAltDuotone } from '@fortawesome/duotone-light-svg-icons/faGamepadAlt';
import clsx from 'clsx';
import type { PlaycastGamepadId } from '@playcastdotio/CoreTypes';

type ButtonClasses = {
  base: string;
  size: string;
  active: string;
  inactive: string;
};

type BeaconGamepadButtonsProps = {
  isGlobal: boolean;
  isAfk: boolean;
  playerSlots: PlaycastGamepadId[];
  hostGamepadIds: number[];
  buttonClasses: ButtonClasses;
  onSlotToggle: (slotIndex: number, enabled: boolean) => void;
  onGamepadAccessChange?: (enabled: boolean) => void;
};

export const BeaconGamepadButtons = ({
  isGlobal,
  isAfk,
  playerSlots,
  hostGamepadIds,
  buttonClasses,
  onSlotToggle,
  onGamepadAccessChange,
}: BeaconGamepadButtonsProps) => {
  const [lastClickTime, setLastClickTime] = useState(0);
  const [lastClickedNum, setLastClickedNum] = useState<number | null>(null);
  const [showRing, setShowRing] = useState<number | null>(null);

  const activePositions = useMemo(() => {
    if (isGlobal) {
      return hostGamepadIds.map((id) => id + 1);
    }
    return playerSlots.map((slot) => slot + 1);
  }, [isGlobal, hostGamepadIds, playerSlots]);

  const handleClick = useCallback(
    (num: number) => {
      const now = Date.now();
      const isActive = activePositions.includes(num);
      const isHostSlot = hostGamepadIds.includes((num - 1) as PlaycastGamepadId);

      if (!isGlobal && !isActive && isHostSlot) return;

      if (now - lastClickTime < 300 && lastClickedNum === num) {
        [1, 2, 3, 4].forEach((n) => {
          if (n !== num) onSlotToggle(n, false);
        });
        if (!isGlobal && !isHostSlot) {
          onSlotToggle(num, true);
          onGamepadAccessChange?.(true);
          setShowRing(num);
          setTimeout(() => setShowRing(null), 1000);
        }
      } else {
        if (isActive && playerSlots.length === 1) {
          onGamepadAccessChange?.(false);
        } else if (!isActive) {
          onGamepadAccessChange?.(true);
        }
        onSlotToggle(num, !isActive);
      }

      setLastClickTime(now);
      setLastClickedNum(num);
    },
    [activePositions, hostGamepadIds, isGlobal, lastClickTime, lastClickedNum, playerSlots.length, onSlotToggle, onGamepadAccessChange],
  );

  const GamepadButton = useCallback(
    ({ num }: { num: number }) => {
      const isActive = activePositions.includes(num);
      const isHostSlot = hostGamepadIds.includes((num - 1) as PlaycastGamepadId);
      const isDisabled = !isGlobal && isHostSlot && !isActive;

      const className = clsx(
        buttonClasses.base,
        isGlobal ? buttonClasses.size : 'p-1 lg:relative lg:size-9',
        'text-3xl relative',
        isDisabled ? 'cursor-not-allowed !bg-red-600/30 hover:bg-slate-600' : '',
        isActive ? buttonClasses.active : buttonClasses.inactive,
        isGlobal && !isActive && 'cursor-default hover:!bg-slate-600 active:!bg-slate-600',
        isGlobal && isActive && 'cursor-default hover:!bg-emerald-400 active:!bg-emerald-400',
        isAfk && isActive && 'z-10 bg-purple-900 hover:bg-purple-900/80 active:bg-purple-900/60 text-purple-400',
      );

      return (
        <div key={num} className='relative'>
          {showRing === num && <div className='absolute inset-0 animate-ping rounded-full bg-emerald-400/80' />}
          <button className={className} onClick={() => handleClick(num)} disabled={isDisabled}>
            {isDisabled && (
              <FontAwesomeIcon
                icon={faLock}
                className='text-darkest/80 absolute left-1/2 top-1/2 size-6 -translate-x-1/2 -translate-y-1/2'
              />
            )}
            <span
              className={clsx(
                'absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2',
                isGlobal ? 'text-4xl' : 'text-3xl',
                isDisabled && 'opacity-20',
              )}
            >
              {num}
            </span>
            <FontAwesomeIcon icon={faGamepadAltSolid} className='text-darker/80 size-10 lg:hidden' />
          </button>
        </div>
      );
    },
    [activePositions, hostGamepadIds, isGlobal, buttonClasses, handleClick, showRing, isAfk],
  );

  return (
    <div className='flex items-center gap-2 px-2 lg:p-0'>
      <FontAwesomeIcon
        icon={isAfk ? faGamepadAltDuotone : faGamepadAltSolid}
        className={clsx(
          'hidden size-12 lg:flex',
          isGlobal ? 'mr-2' : '',
          activePositions.length > 0 ? 'text-emerald-400' : 'text-slate-400',
          isAfk && 'z-10 text-purple-400',
        )}
        style={{ '--fa-secondary-color': '#581c87', '--fa-secondary-opacity': '1' } as React.CSSProperties}
      />
      {[1, 2, 3, 4].map((num) => (
        <GamepadButton key={num} num={num} />
      ))}
    </div>
  );
};
